import { filterBundleComponentLineItems } from "@redotech/order-tracking-common/line-item-property-display";
import { OrderTrackingShipmentStatus } from "@redotech/order-tracking-common/order-tracking-types";
import type {
  TrackingPageLineItem,
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/order-tracking-common/tracking-page-order-definition";
import {
  findShipmentIndexById,
  TRACKING_PAGE_SHIPMENT_ID_PARAM,
} from "@redotech/order-tracking-common/tracking-page-shipment-link";
import { TrackingPageEnvironment } from "../../context/tracking-page-context";

export interface LineItemVisibilityOptions {
  hideVirtualItems?: boolean;
  hideZeroDollarItems?: boolean;
  hideBundleComponents?: boolean;
}

/**
 * A line item's effective price is its discounted price when a line-level
 * discount applies, otherwise its undiscounted price. A missing price is
 * distinct from $0 and is never treated as zero-dollar.
 */
export function isZeroDollarLineItem(item: TrackingPageLineItem): boolean {
  const effectivePrice = item.discountedPrice ?? item.undiscountedPrice;
  return effectivePrice?.amount === 0;
}

export function filterVisibleLineItems(
  items: TrackingPageLineItem[],
  {
    hideVirtualItems,
    hideZeroDollarItems,
    hideBundleComponents,
  }: LineItemVisibilityOptions,
): TrackingPageLineItem[] {
  return filterBundleComponentLineItems(items, hideBundleComponents).filter(
    (item) => {
      if (hideVirtualItems && item.requiresShipping === false) {
        return false;
      }
      if (hideZeroDollarItems && isZeroDollarLineItem(item)) {
        return false;
      }
      return true;
    },
  );
}

/**
 * Common shipment selection logic used across multiple blocks.
 * Falls back to first shipment with tracking if requested index is invalid.
 */
export function getSelectedShipmentWithFallback(
  shipments: TrackingPageShipment[] | undefined,
  shipmentIndex: string | undefined,
): TrackingPageShipment | undefined {
  if (!shipments?.length) {
    return undefined;
  }

  const index = shipmentIndex ? parseInt(shipmentIndex) : -1;
  const requestedShipment = shipments[index];

  if (requestedShipment) {
    return requestedShipment;
  }

  // Index was invalid or out of bounds — fall back to first shipment with tracking
  return shipments.find((s) => s.trackingCode) ?? shipments[0];
}

export function getUnfulfilledLineItems(
  order: TrackingPageOrder,
): TrackingPageLineItem[] {
  const shippedQty: { [itemId: string]: number } = {};
  for (const shipment of order.shipments ?? []) {
    // A cancelled fulfillment returns its items to the unfulfilled pool.
    if (shipment.status === OrderTrackingShipmentStatus.SHIPMENT_CANCELLED) {
      continue;
    }
    for (const item of shipment.lineItems ?? []) {
      shippedQty[item.id] = (shippedQty[item.id] ?? 0) + item.quantity;
    }
  }
  const result: TrackingPageLineItem[] = [];
  for (const item of order.lineItems ?? []) {
    // Virtual items never ship, so they are not "not yet shipped".
    if (item.requiresShipping === false) {
      continue;
    }
    const unfulfilledQty = item.quantity - (shippedQty[item.id] ?? 0);
    if (unfulfilledQty > 0) {
      result.push({ ...item, quantity: unfulfilledQty });
    }
  }
  return result;
}

/**
 * Get shipment index with builder mode fallback
 */
/** Blocks render with or without a Router, so the link's shipment id is read from the page URL. */
export function getShipmentIdFromLocation(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return new URLSearchParams(window.location.search).get(
    TRACKING_PAGE_SHIPMENT_ID_PARAM,
  );
}

export function getShipmentIndex(
  paramsShipmentIndex: string | undefined,
  environment: TrackingPageEnvironment,
  order?: TrackingPageOrder,
  shipmentId?: string | null,
): string {
  if (environment === TrackingPageEnvironment.BUILDER) {
    if (!order) {
      return "0";
    }
    const index = order.shipments.findIndex(
      (shipment) => shipment.trackingCode,
    );
    return index === -1 ? "0" : index.toString();
  }
  if (shipmentId) {
    const index = findShipmentIndexById(order?.shipments, shipmentId);
    if (index !== undefined) {
      return index.toString();
    }
  } else if (paramsShipmentIndex) {
    return paramsShipmentIndex;
  }
  // No explicit selection — don't land the customer on a cancelled package.
  const index = (order?.shipments ?? []).findIndex(
    (shipment) =>
      shipment.status !== OrderTrackingShipmentStatus.SHIPMENT_CANCELLED,
  );
  return index === -1 ? "0" : index.toString();
}

/** Marks an element in Custom HTML that opens a shipment when clicked. */
export const SHIPMENT_LINK_ATTRIBUTE = "data-redo-shipment-id";

/** The index of the shipment a click inside Custom HTML asks to open, if any. */
export function clickedShipmentIndex(
  target: EventTarget | null,
  shipments: readonly { id: string }[] | undefined,
): number | undefined {
  if (!(target instanceof Element)) {
    return undefined;
  }
  const shipmentId = target
    .closest(`[${SHIPMENT_LINK_ATTRIBUTE}]`)
    ?.getAttribute(SHIPMENT_LINK_ATTRIBUTE);
  return findShipmentIndexById(shipments, shipmentId);
}
